import { zodResolver } from "@hookform/resolvers/zod"
import { Button, InputText, InputNumber, InputValidation, Modal } from "@jobber/components"
import { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import TimeInput from "./TimePicker"

type Props = {
  onSubmit: (values: TimeWindowFormResult) => void
}

type RefFields = {
 openModal: (timeWindow?: any) => void
}

type TimeWindowFormResult = {
  index?: number | null,
  name: string
  timeFrom: string
  timeTo: string
  slotsPerWindow: number
}

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  // match the time format in the backend:
  timeFrom: z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, {
    message: "Time must be in 24h format (e.g. '09:00' or '14:00')"
  }),
  timeTo: z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, {
    message: "Time must be in 24h format (e.g. '09:00' or '14:00')"
  }),
  slotsPerWindow: z.number().min(1, "Must have at least 1 slot").max(100, "Cannot exceed 100 slots"),
})

const CreateTimeWindowModal = forwardRef(({ onSubmit }: Props, ref: React.Ref<RefFields>) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [index, setIndex] = useState<number | null>(null)
  const [timeWindowData, setTimeWindowData] = useState<any>(null)

  useImperativeHandle(ref, () => ({
    openModal: (timeWindow?: TimeWindowFormResult) => {
      if (timeWindow) {
        if (timeWindow.index === undefined || timeWindow.index === null) timeWindow.index = null
        else setIndex(timeWindow.index)
        setTimeWindowData(timeWindow)
        form.setValue('name', timeWindow.name)
        form.setValue('timeFrom', timeWindow.timeFrom)
        form.setValue('timeTo', timeWindow.timeTo)
        form.setValue('slotsPerWindow', timeWindow.slotsPerWindow)
      }
      setModalOpen(true)
    }
  }))
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      timeFrom: '',
      timeTo: '',
      slotsPerWindow: 1
    }
  })

  useEffect(() => {
    if (!modalOpen) {
      setIndex(null)
      setTimeWindowData(null)
      form.reset()
    }
  }, [modalOpen, form])

  const onFormSubmit = (values: z.infer<typeof formSchema>) => {
    const returnObj: TimeWindowFormResult = {...values}
    if (index !== null) returnObj.index = index
    onSubmit(returnObj)
    setModalOpen(false)
  }
  return (
    <Modal
      title={index ? `Edit "${timeWindowData.name}" Time Window` : "Create new Time Window"}
      open={modalOpen}
      onRequestClose={() => setModalOpen(false)}
    >
      <form
        style={{ padding: '10px 20px', display: 'flex', flexDirection: 'column', gap: '20px' }}
        onSubmit={form.handleSubmit(onFormSubmit)}
      >
        <div>
          <InputText
            placeholder={"Time window name"}
            value={form.watch('name')}
            onChange={(val) => form.setValue('name', val as string)}
          />
          {form.formState.errors.name && <InputValidation message={form.formState.errors.name.message as string} />}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{width: '100%'}}>
            <TimeInput
              label={"Start time"}
              value={form.watch('timeFrom')}
              onChange={(val) => form.setValue('timeFrom', val as string)}
            />
            {form.formState.errors.timeFrom && <InputValidation message={form.formState.errors.timeFrom?.message as string} />}
          </div>
          <div style={{width: '100%'}}>
            <TimeInput
              label={"End time"}
              value={form.watch('timeTo')}
              onChange={(val) => form.setValue('timeTo', val as string)}
            />
            {form.formState.errors.timeTo && <InputValidation message={form.formState.errors.timeTo?.message as string} />}
          </div>
        </div>
        <div>
          <InputNumber
            placeholder="Number of slots"
            value={form.watch('slotsPerWindow')}
            onChange={(val) => form.setValue('slotsPerWindow', parseInt(val as string) || 1)}
          />
          {form.formState.errors.slotsPerWindow &&
            <InputValidation message={form.formState.errors.slotsPerWindow.message as string} />
          }
        </div>
        <div style={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            submit
            variation="learning"
            label={ index ? 'Save' : 'Create' }
            loading={false}
            disabled={false}
          />
        </div>
      </form>
    </Modal>
  )
})

export default CreateTimeWindowModal

export type {RefFields, TimeWindowFormResult}